import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonNote } from '@/api/client';
import { api } from '@/api/client';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import { CloseIcon, AddIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { useDebouncePerKey } from '@/hooks/useDebouncePerKey';
import { Button } from '@/components/ui/Button/Button';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { generateUUID } from '@/utils/generateUUID';
import { TEMP_NOTE_PREFIX, isTempNote } from '@/utils/projectNotes';
import { ProjectNotesPopoverNoteItem } from './ProjectNotesPopoverNoteItem';
import { sortProjectNotesNewestFirst } from './projectNotesPopoverUtils';
import {
  PROJECT_NOTES_AUTO_SAVE_DEBOUNCE_MS,
  PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR,
} from './projectNotesPopoverConstants';
import { getPersonNotesQueryOptions } from './personNotesPopoverQuery';
import type { PersonNotesPopoverServerProps } from './ProjectNotesPopover.types';
import '@/components/ui/PopoverInlineLoading.css';
import './ProjectNotesPopover.css';

/**
 * Заметки персоны (чтец): та же разметка, стили и логика (debounce, temp-заметки, анимация),
 * что и ProjectNotesPopoverServer.
 */
export function PersonNotesPopoverServer({
  open,
  onClose,
  anchorEl,
  personId,
  titleName,
}: PersonNotesPopoverServerProps) {
  const queryClient = useQueryClient();
  const { showError: showToastError } = useToast();
  const { user } = useAuth();
  const bumpReadersTableNotesSummaries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['readersTableSummaries'] });
  }, [queryClient]);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<Map<string, string>>(new Map());
  const draftContentRef = useRef<Map<string, string>>(new Map());
  draftContentRef.current = draftContent;
  const dirtyIdsRef = useRef<Set<string>>(new Set());
  const creatingTempIdsRef = useRef<Set<string>>(new Set());
  const pendingContentAfterCreateRef = useRef<Map<string, string>>(new Map());

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  const {
    data: notes,
    isPending: notesPending,
    isError: notesError,
    error: notesFetchError,
  } = useQuery({
    ...getPersonNotesQueryOptions(personId),
    enabled: shouldRender && !!personId,
  });

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'x',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    excludeSelector: PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR,
    enabled: shouldRender,
    capture: false,
  });

  const removeTempNote = useCallback(
    (tempId: string) => {
      queryClient.setQueryData<PersonNote[]>(['personNotes', personId], (list) =>
        (list ?? []).filter((n) => n.note_id !== tempId)
      );
    },
    [queryClient, personId]
  );

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose]);

  const notesFetchErrorToastedRef = useRef(false);
  useEffect(() => {
    if (!shouldRender) {
      notesFetchErrorToastedRef.current = false;
      return;
    }
    if (!notesError) {
      notesFetchErrorToastedRef.current = false;
      return;
    }
    if (!notesFetchError || notesFetchErrorToastedRef.current) return;
    notesFetchErrorToastedRef.current = true;
    showToastError((notesFetchError as Error).message || 'Не удалось загрузить заметки');
  }, [shouldRender, notesError, notesFetchError, showToastError]);

  const updateMutation = useMutation({
    mutationFn: ({ noteId, note_content }: { noteId: string; note_content: string }) =>
      api.persons.updateNote(personId, noteId, { note_content }),
    onMutate: async ({ noteId, note_content }) => {
      await queryClient.cancelQueries({ queryKey: ['personNotes', personId] });
      const previousNotes = queryClient.getQueryData<PersonNote[]>(['personNotes', personId]);
      queryClient.setQueryData<PersonNote[]>(['personNotes', personId], (old) =>
        (old ?? []).map((n) => (n.note_id === noteId ? { ...n, note_content } : n))
      );
      return { previousNotes };
    },
    onError: (err, _vars, context) => {
      if (context?.previousNotes !== undefined) {
        queryClient.setQueryData(['personNotes', personId], context.previousNotes);
      }
      showToastError((err as Error).message || 'Ошибка');
    },
    onSuccess: (data) => {
      queryClient.setQueryData<PersonNote[]>(['personNotes', personId], (old) =>
        (old ?? []).map((n) => (n.note_id === data.note_id ? data : n))
      );
      setDraftContent((prev) => {
        const current = prev.get(data.note_id);
        if (current !== undefined && current === data.note_content) {
          const next = new Map(prev);
          next.delete(data.note_id);
          return next;
        }
        return prev;
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => api.persons.deleteNote(personId, noteId),
    onMutate: async (noteId: string) => {
      await queryClient.cancelQueries({ queryKey: ['personNotes', personId] });
      dirtyIdsRef.current.delete(noteId);
      const previousNotes = queryClient.getQueryData<PersonNote[]>(['personNotes', personId]);
      queryClient.setQueryData<PersonNote[]>(['personNotes', personId], (old) =>
        (old ?? []).filter((n) => n.note_id !== noteId)
      );
      return { previousNotes };
    },
    onError: (err, _noteId, context) => {
      if (context?.previousNotes !== undefined) {
        queryClient.setQueryData(['personNotes', personId], context.previousNotes);
      }
      showToastError((err as Error).message || 'Ошибка');
    },
    onSuccess: () => {
      bumpReadersTableNotesSummaries();
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: ({
      note_content,
      created_by,
    }: {
      note_content: string;
      tempId: string;
      created_by?: string | null;
    }) => api.persons.createNote(personId, { note_content, created_by }),
    onMutate: async ({ tempId, note_content, created_by }) => {
      await queryClient.cancelQueries({ queryKey: ['personNotes', personId] });
      const previousNotes = queryClient.getQueryData<PersonNote[]>(['personNotes', personId]);
      const currentNotes = previousNotes ?? [];
      creatingTempIdsRef.current.add(tempId);
      const optimisticNote: PersonNote = {
        note_id: tempId,
        person_id: personId,
        note_content,
        created_at: new Date().toISOString(),
        created_by: created_by ?? null,
        display_order: currentNotes.length,
      };
      queryClient.setQueryData<PersonNote[]>(['personNotes', personId], [optimisticNote, ...currentNotes]);
      return { previousNotes, tempId };
    },
    onError: (err, _vars, context) => {
      if (context?.tempId != null) {
        pendingContentAfterCreateRef.current.delete(context.tempId);
      }
      if (context?.previousNotes !== undefined) {
        queryClient.setQueryData(['personNotes', personId], context.previousNotes);
      }
      showToastError((err as Error).message || 'Ошибка');
    },
    onSuccess: (data, _vars, context) => {
      const tempId = context?.tempId;
      if (!tempId) return;
      queryClient.setQueryData<PersonNote[]>(['personNotes', personId], (old) =>
        (old ?? []).map((n) => (n.note_id === tempId ? data : n))
      );
      setDraftContent((prev) => {
        const next = new Map(prev);
        next.delete(tempId);
        const pending = pendingContentAfterCreateRef.current.get(tempId);
        if (pending !== undefined && pending !== '') {
          next.set(data.note_id, pending);
        }
        return next;
      });
      const pendingRaw = pendingContentAfterCreateRef.current.get(tempId);
      pendingContentAfterCreateRef.current.delete(tempId);
      if (pendingRaw !== undefined && pendingRaw !== data.note_content) {
        updateMutation.mutate({ noteId: data.note_id, note_content: pendingRaw.trim() });
      }
      bumpReadersTableNotesSummaries();
    },
    onSettled: (_data, _err, variables) => {
      creatingTempIdsRef.current.delete(variables.tempId);
    },
  });

  const performSaveNote = useCallback(
    (noteId: string) => {
      const raw = draftContentRef.current.get(noteId) ?? '';
      const content = raw.trim();
      const currentNotes = queryClient.getQueryData<PersonNote[]>(['personNotes', personId]) ?? [];
      const note = currentNotes.find((n) => n.note_id === noteId);
      if (!note) return;

      if (isTempNote(noteId)) {
        if (creatingTempIdsRef.current.has(noteId)) {
          pendingContentAfterCreateRef.current.set(noteId, raw);
          return;
        }
        if (content) {
          createMutation.mutate({
            note_content: content,
            tempId: noteId,
            created_by: user?.id ?? null,
          });
        } else {
          removeTempNote(noteId);
          setDraftContent((prev) => {
            const next = new Map(prev);
            next.delete(noteId);
            return next;
          });
        }
      } else {
        if (raw === note.note_content) return;
        dirtyIdsRef.current.delete(noteId);
        updateMutation.mutate({ noteId, note_content: content });
      }
    },
    [personId, createMutation, updateMutation, removeTempNote, user?.id, queryClient]
  );

  const [scheduleSave, flush] = useDebouncePerKey(PROJECT_NOTES_AUTO_SAVE_DEBOUNCE_MS, performSaveNote);

  useEffect(() => {
    if (open) {
      dirtyIdsRef.current.clear();
      setDraftContent(new Map());
    } else {
      flush();
    }
  }, [open, flush]);

  const handleAddNote = () => {
    const tempId = `${TEMP_NOTE_PREFIX}${generateUUID()}`;
    createMutation.mutate({
      note_content: '',
      tempId,
      created_by: user?.id ?? null,
    });
  };

  const handleNoteChange = useCallback(
    (noteId: string, content: string) => {
      if (!isTempNote(noteId)) dirtyIdsRef.current.add(noteId);
      setDraftContent((prev) => {
        const next = new Map(prev);
        next.set(noteId, content);
        return next;
      });
      scheduleSave(noteId);
    },
    [scheduleSave]
  );

  const getNoteContent = (note: PersonNote) => draftContent.get(note.note_id) ?? note.note_content;

  const handleDelete = useCallback(
    (noteId: string) => {
      if (deletingId) return;
      if (!window.confirm('Удалить заметку?')) return;
      setDeletingId(noteId);
      deleteMutation.mutate(noteId);
    },
    [deletingId, deleteMutation]
  );

  const notesSorted = useMemo(() => sortProjectNotesNewestFirst(notes ?? []), [notes]);

  const displayTitle = titleName.trim() || 'Чтец';

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-notes-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="person-notes-popover-title"
        >
          <div className="project-notes-popover__header">
            <h2 id="person-notes-popover-title" className="project-notes-popover__title">
              Заметки: {displayTitle}
            </h2>
            <IconButton
              aria-label="Закрыть"
              onClick={requestClose}
              size="m"
              iconSize={20}
              variant="danger"
              className="project-notes-popover__close"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="project-notes-popover__body">
            <OverlayScrollArea
              className="project-notes-popover__scroll"
              osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
            >
              <div className="project-notes-popover__scroll-pad">
                {notesPending ? (
                  <div className="project-popover-inline-loading" aria-busy="true" aria-live="polite">
                    <span className="project-popover-inline-loading__spinner" aria-label="Загрузка заметок" />
                  </div>
                ) : notesError ? (
                  <div className="project-notes-popover__empty" role="alert">
                    <p>{(notesFetchError as Error)?.message || 'Не удалось загрузить заметки'}</p>
                  </div>
                ) : (
                  <ul className="project-notes-popover__list">
                    {notesSorted.length === 0 ? (
                      <li className="project-notes-popover__empty">
                        <i className="fa-solid fa-pen-clip project-notes-popover__empty-icon" aria-hidden="true" />
                        <p>Нет заметок</p>
                      </li>
                    ) : (
                      notesSorted.map((note) => (
                        <ProjectNotesPopoverNoteItem
                          key={note.note_id}
                          note={note}
                          content={getNoteContent(note)}
                          onContentChange={handleNoteChange}
                          onDelete={handleDelete}
                          isDeleting={deletingId === note.note_id}
                          deleteDisabled={deleteMutation.isPending}
                        />
                      ))
                    )}
                  </ul>
                )}
              </div>
            </OverlayScrollArea>
            <div className="project-notes-popover__add-btn-wrap">
              <Button
                type="button"
                variant="outlined"
                onClick={handleAddNote}
                disabled={notesPending || createMutation.isPending || updateMutation.isPending}
                className="project-notes-popover__add-btn"
              >
                <AddIcon size={18} />
                Добавить заметку
              </Button>
            </div>
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return shouldRender && personId ? createPortal(popoverContent, document.body) : null;
}
